<template>
  <section class="use-cases-section">
    <!-- Section Header -->
    <div class="use-cases-header">
      <h3 class="use-cases-title">
        Crypto Gifts Fit <i>Every Story</i>
      </h3>
      <p class="use-cases-subtitle">
        From long-distance friendships to global teams
      </p>
    </div>

    <!-- Emoji Paginator -->
    <div class="use-cases-content">
      <!-- Emoji Navigation -->
      <div class="emoji-paginator">
        <button
          v-for="(useCase, index) in useCases"
          :key="useCase.id"
          :class="['emoji-button', { active: activeIndex === index }]"
          @click="activeIndex = index"
        >
          <span class="emoji-icon">{{ useCase.icon }}</span>
        </button>
      </div>

      <!-- Active Use Case Content -->
      <transition name="fade-slide" mode="out-in">
        <div :key="activeIndex" class="use-case-content">
          <h4 class="use-case-title">
            {{ useCases[activeIndex].title }}
          </h4>
          <p class="use-case-description">
            {{ useCases[activeIndex].description }}
          </p>
        </div>
      </transition>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const useCases = [
  {
    id: 1,
    icon: '💞',
    title: 'For long-distance connections',
    description: 'Let your friends or family know you care. Send love that travels faster than mail or banks, and without the queues'
  },
  {
    id: 2,
    icon: '🎉',
    title: 'For birthdays & celebrations',
    description: 'Celebrate birthdays, anniversaries, and special occasions with truly high-end digital gifts'
  },
  {
    id: 3,
    icon: '🐹',
    title: 'For crypto newcomers',
    description: 'Help someone start their crypto journey safely. They don\'t need prior knowledge or setup'
  },
  {
    id: 4,
    icon: '🧑‍💻',
    title: 'For remote teams',
    description: 'Send bonuses or thank-you gifts—faster, borderless, and with easy tracking'
  },
  {
    id: 5,
    icon: '📣',
    title: 'For giveaways & communities',
    description: 'Share crypto rewards in bulk with your followers or event attendees'
  }
]

const activeIndex = ref(0)
</script>

<style scoped>
.use-cases-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 16px;
  gap: 24px;
  background: #FFFFFF;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
}

.use-cases-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
}

.use-cases-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: 0.01em;
  color: #182855;
}

.use-cases-subtitle {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.01em;
  color: #182855;
  align-self: stretch;
}

/* Emoji Paginator */
.use-cases-content {
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: 24px;
  align-self: stretch;
}

.emoji-paginator {
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
}

.emoji-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 16px;
  position: relative;
}

.emoji-icon {
  font-size: 32px;
  display: block;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: grayscale(0.7) opacity(0.5);
}

.emoji-button.active .emoji-icon {
  filter: grayscale(0) opacity(1);
  transform: scale(1.2);
}

.emoji-button:hover .emoji-icon {
  filter: grayscale(0.3) opacity(0.8);
  transform: scale(1.1);
}

/* Use Case Content */
.use-case-content {
  display: flex;
  flex-direction: column;
  align-items: left;
  text-align: left;
  gap: 12px;
  min-height: 120px;
}

.use-case-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.01em;
  color: #182855;
}

.use-case-description {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.005em;
  color: #3A55A6;
  max-width: 500px;
}

/* Content Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
